import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AdjustmentsHorizontalIcon, ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  getEvaluationQuestionSettings,
  getMaintenanceCampaignEvaluation,
  getMaintenanceContract,
  maintenanceContractCustomerLabel,
  saveEvaluationQuestionSettings,
  type CampaignEvaluationResult,
  type MaintenanceContract,
} from "@/lib/wartungen";
import { formatSchedule } from "./lib/campaignView";
import {
  assetDisplayLabel,
  buildEvaluationModel,
  buildEvaluationQuestions,
  defaultSelectedQuestionKeys,
  normalizeSelectedQuestionKeys,
  type EvaluationStatusFilter,
} from "./auswertung/evaluationModel";
import {
  AssetEvaluationTable,
  DefectsSection,
  EvaluationHero,
  GradeDistribution,
  MaterialSection,
  Panel,
  QuestionBreakdownSection,
} from "./auswertung/EvaluationSections";
import { QuestionSettingsDialog } from "./auswertung/QuestionSettingsDialog";
import "./auswertung/auswertung.css";

function num(params: URLSearchParams, key: string): number | null {
  const n = Number(params.get(key));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Auswertung einer Wartung (Kampagne): gewartete Objekte, Noten, Mängel,
 * Material und frei wählbare Fragen des Wartungsformulars.
 */
export function WartungAuswertungPage() {
  const [params] = useSearchParams();
  const routerLocation = useLocation();
  const navigate = useNavigate();

  const campaignId = num(params, "campaign_id");
  const contractId = num(params, "contract_id");
  const typeKeyParam = (params.get("type_key") || params.get("maintenance_type_key") || "").trim();

  /** Zurück in die Wartungshülle mit identischem Kontext. */
  const huelleHref = useMemo(() => {
    const usp = new URLSearchParams(routerLocation.search);
    usp.delete("asset_id");
    usp.delete("fresh");
    const qs = usp.toString();
    return `/wartungen/huelle-detail${qs ? `?${qs}` : ""}`;
  }, [routerLocation.search]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignEvaluationResult | null>(null);
  const [contract, setContract] = useState<MaintenanceContract | null>(null);
  const [savedKeys, setSavedKeys] = useState<string[] | null | undefined>(undefined);
  const [selectedKeys, setSelectedKeys] = useState<string[] | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const [subLocationFilter, setSubLocationFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<EvaluationStatusFilter>("all");
  const [textFilter, setTextFilter] = useState("");
  const [showAnswerColumns, setShowAnswerColumns] = useState(false);

  const typeKey = useMemo(
    () => typeKeyParam || String(result?.campaign?.maintenance_type_key ?? "").trim(),
    [typeKeyParam, result],
  );

  const load = useCallback(
    async (mode: "initial" | "refresh") => {
      if (!campaignId) {
        setLoading(false);
        setError("Diese Auswertung kann nur aus einer Wartung geöffnet werden (campaign_id fehlt).");
        return;
      }
      if (mode === "initial") setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const data = await getMaintenanceCampaignEvaluation(campaignId);
        setResult(data);
        const resolvedType = typeKeyParam || String(data?.campaign?.maintenance_type_key ?? "").trim();
        if (mode === "initial") {
          try {
            setSavedKeys(await getEvaluationQuestionSettings(resolvedType));
          } catch {
            setSavedKeys(null);
          }
        }
        const cid = contractId ?? Number(data?.campaign?.contract_id);
        if (Number.isFinite(cid) && cid > 0 && !contract) {
          getMaintenanceContract(cid)
            .then(setContract)
            .catch(() => setContract(null));
        }
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.status === 404
              ? "Die Wartung wurde nicht gefunden."
              : err.message
            : "Auswertung konnte nicht geladen werden.",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [campaignId, contractId, typeKeyParam, contract],
  );

  useEffect(() => {
    void load("initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const questions = useMemo(() => buildEvaluationQuestions(result), [result]);

  // Gespeicherte Auswahl anwenden, sobald Schema und Einstellungen da sind.
  useEffect(() => {
    if (!result || savedKeys === undefined) return;
    setSelectedKeys(savedKeys == null ? defaultSelectedQuestionKeys(questions) : normalizeSelectedQuestionKeys(savedKeys, questions));
  }, [result, savedKeys, questions]);

  const effectiveSelected = useMemo(
    () => selectedKeys ?? defaultSelectedQuestionKeys(questions),
    [selectedKeys, questions],
  );

  const model = useMemo(
    () =>
      buildEvaluationModel(result, {
        selectedQuestionKeys: effectiveSelected,
        subLocationId: subLocationFilter === "all" ? null : subLocationFilter === "none" ? "none" : Number(subLocationFilter),
        statusFilter,
        textFilter,
      }),
    [result, effectiveSelected, subLocationFilter, statusFilter, textFilter],
  );

  const assetLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const asset of result?.assets ?? []) map.set(asset.asset_id, assetDisplayLabel(asset));
    return map;
  }, [result]);
  const assetLabel = useCallback((assetId: number) => assetLabelById.get(assetId) ?? `#${assetId}`, [assetLabelById]);

  const campaign = (result?.campaign ?? {}) as Record<string, unknown>;
  const title = String(campaign.name || "").trim() || "Wartung";
  const typeLabel = String(campaign.maintenance_type_name || typeKey || "").trim();
  const scheduleLabel = formatSchedule(String(campaign.date_mode || ""), String(campaign.date_value || ""));
  const locationLabel = String(campaign.building_name || campaign.location_name || "").trim();
  const customerLabel = contract ? maintenanceContractCustomerLabel(contract) : "";
  const subtitle = [typeLabel, scheduleLabel !== "–" ? scheduleLabel : "", locationLabel, customerLabel].filter(Boolean).join(" · ");
  const objectLabel = "Objekte";

  const listFilterActive = statusFilter !== "all" || textFilter.trim() !== "" || subLocationFilter !== "all";

  function openAssetInHuelle(assetId: number) {
    const usp = new URLSearchParams(routerLocation.search);
    usp.delete("fresh");
    usp.set("asset_id", String(assetId));
    navigate(`/wartungen/huelle-detail?${usp.toString()}`);
  }

  async function handleSaveQuestions(next: string[]) {
    setSettingsError(null);
    setSelectedKeys(next);
    try {
      await saveEvaluationQuestionSettings(typeKey, next);
      setSavedKeys(next);
    } catch (err) {
      setSettingsError(
        err instanceof ApiError ? `Fragenauswahl konnte nicht gespeichert werden: ${err.message}` : "Fragenauswahl konnte nicht gespeichert werden.",
      );
    }
  }

  return (
    <AppShell
      title={`Auswertung: ${title}`}
      subtitle={subtitle || undefined}
      back={{ to: campaignId ? huelleHref : "/wartungen", label: campaignId ? "zurück zur Wartung" : "zurück zur Übersicht" }}
      actions={
        result ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void load("refresh")}
              loading={refreshing}
              leftIcon={<ArrowPathIcon className="h-4 w-4" aria-hidden />}
            >
              Aktualisieren
            </Button>
            <Button
              type="button"
              onClick={() => setSettingsOpen(true)}
              leftIcon={<AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden />}
              title="Fragen für die Auswertung auswählen"
            >
              Fragen auswählen
              {questions.length ? (
                <span className="ml-1.5 rounded-full bg-white/20 px-1.5 text-xs tabular-nums">
                  {effectiveSelected.length}/{questions.length}
                </span>
              ) : null}
            </Button>
          </div>
        ) : undefined
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {settingsError ? (
        <Alert variant="warning" className="mb-4">
          {settingsError}
        </Alert>
      ) : null}

      {loading ? (
        <div className="wa-panel flex items-center gap-2 px-4 py-8 text-sm text-label-secondary">
          <Spinner /> Lade Auswertung …
        </div>
      ) : !result ? null : (
        <div className="space-y-4">
          <EvaluationHero model={model} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} objectLabel={objectLabel} />

          {questions.length === 0 ? (
            <Alert variant="info">
              Für dieses Wartungsformular sind keine auswertbaren Inspektionsfragen hinterlegt. Noten und Status werden trotzdem ausgewertet.
            </Alert>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <GradeDistribution buckets={model.gradeBuckets} gradedAssets={model.summary.gradedAssets} ungradedDone={model.ungradedDone} />
            <DefectsSection groups={model.defectGroups} assetLabel={assetLabel} onOpenAsset={openAssetInHuelle} />
          </div>

          <MaterialSection aggregates={model.materialAggregates} assetLabel={assetLabel} />

          <QuestionBreakdownSection breakdowns={model.questionBreakdowns} assetLabel={assetLabel} onOpenSettings={() => setSettingsOpen(true)} />

          <Panel
            title={`${objectLabel} mit Noten`}
            hint={
              model.rows.length === model.summary.totalAssets
                ? `${model.summary.totalAssets} ${objectLabel}`
                : `${model.rows.length} von ${model.summary.totalAssets} ${objectLabel}`
            }
            actions={
              <label className="inline-flex items-center gap-2 text-xs text-label-secondary">
                <input
                  type="checkbox"
                  checked={showAnswerColumns}
                  onChange={(e) => setShowAnswerColumns(e.target.checked)}
                  disabled={!effectiveSelected.length}
                />
                Antworten der ausgewählten Fragen als Spalten
              </label>
            }
          >
            <div className="wa-toolbar mb-3">
              <div className="wa-toolbar__group">
                <select
                  className="eh-input wa-toolbar__select"
                  value={subLocationFilter}
                  onChange={(e) => setSubLocationFilter(e.target.value)}
                  aria-label="Bereich filtern"
                >
                  <option value="all">Alle Bereiche</option>
                  {model.subLocations.map((sl) => (
                    <option key={sl.id == null ? "none" : sl.id} value={sl.id == null ? "none" : String(sl.id)}>
                      {sl.name} ({sl.count})
                    </option>
                  ))}
                </select>
                <select
                  className="eh-input wa-toolbar__select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as EvaluationStatusFilter)}
                  aria-label="Status filtern"
                >
                  <option value="all">Alle Status</option>
                  <option value="done">Nur gewartete</option>
                  <option value="issues">Nur mit Mängeln</option>
                  <option value="open">Nur ausstehende</option>
                </select>
                {listFilterActive ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                    onClick={() => {
                      setStatusFilter("all");
                      setTextFilter("");
                      setSubLocationFilter("all");
                    }}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                    Filter zurücksetzen
                  </button>
                ) : null}
              </div>
              <div className="relative w-full sm:w-72">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary" aria-hidden />
                <input
                  className="eh-input w-full pl-9"
                  placeholder="Kennung oder Bezeichnung …"
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                  aria-label="Objekte durchsuchen"
                />
              </div>
            </div>
            <AssetEvaluationTable
              rows={model.rows}
              selectedQuestions={model.selectedQuestions}
              showAnswerColumns={showAnswerColumns}
              objectLabel={objectLabel}
              onOpenAsset={(row) => openAssetInHuelle(row.assetId)}
              emptyText={
                listFilterActive ? (
                  <>
                    Kein Objekt passt zu den Filtern.{" "}
                    <button
                      type="button"
                      className="font-medium text-accent hover:underline"
                      onClick={() => {
                        setStatusFilter("all");
                        setTextFilter("");
                        setSubLocationFilter("all");
                      }}
                    >
                      Filter zurücksetzen
                    </button>
                  </>
                ) : (
                  "Diese Wartung enthält noch keine Objekte."
                )
              }
            />
          </Panel>
        </div>
      )}

      <QuestionSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        questions={questions}
        selected={effectiveSelected}
        onSave={handleSaveQuestions}
      />
    </AppShell>
  );
}
